import ArrowLeft16 from '@carbon/icons-react/es/arrow--left/16';
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import Export16 from '@carbon/icons-react/es/export/16';
import ZoomIn16 from '@carbon/icons-react/es/zoom--in/16';
import ZoomOut16 from '@carbon/icons-react/es/zoom--out/16';
import { Dropdown, Tooltip } from 'antd';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { SearchInput } from 'components/atoms/input';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import {
  getUserSelectablePresetId,
  ViewPreset,
} from 'components/organisms/resource-management/scheduler-config/view-presets/view-presets';
import { useTranslation } from 'hooks/use-translation';

const ButtonGroup = styled.div`
  margin-left: var(--layout-03);
  display: flex;
  align-items: center;
`;
const Search = styled(SearchInput)`
  min-width: 240px;
  margin-right: var(--spacing-03);
`;

interface Props {
  zoomPresets: ViewPreset[];
  viewPresetIds: ViewPresetId[];
  currentPreset: ViewPresetId;
  searchText: string;
  onViewPresetChange: (presetId: ViewPresetId) => void;
  zoomIn?: () => void;
  zoomOut?: () => void;
  shiftNext?: () => void;
  shiftPrevious?: () => void;
  goToToday?: () => void;
  onExport?: () => void;
  onSearchChange: (text: string) => void;
}
export const SecondaryToolbar: FC<Props> = ({
  zoomPresets,
  viewPresetIds,
  currentPreset,
  searchText,
  onViewPresetChange,
  zoomIn,
  zoomOut,
  shiftNext,
  shiftPrevious,
  goToToday,
  onSearchChange,
  onExport,
}) => {
  const zoomPresetIds = zoomPresets.map(({ id }) => id as ViewPresetId);

  const disableZoomOut = useMemo(
    () => zoomPresetIds[0] === currentPreset,
    [currentPreset, zoomPresetIds],
  );

  const disableZoomIn = useMemo(
    () => zoomPresetIds[zoomPresetIds.length - 1] === currentPreset,
    [currentPreset, zoomPresetIds],
  );

  return (
    <>
      <ButtonGroup>
        <Search
          direction="right"
          size="small"
          tooltip={<FormattedMessage id="search" />}
          placeholder={useTranslation('placeholder.search')}
          searchButtonProps={{ type: 'text' }}
          value={searchText}
          onChange={(e) => {
            onSearchChange(e.target.value);
          }}
        />
        <Dropdown
          overlay={
            <Menu
              selectedKeys={[getUserSelectablePresetId(currentPreset)]}
              onClick={({ key }) => onViewPresetChange(key as ViewPresetId)}
            >
              {viewPresetIds.map((id) => (
                <Menu.Item key={id}>
                  <FormattedMessage id={id} />
                </Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button
            type="default"
            size="small"
            icon={<ChevronDown16 />}
            iconPlacement="right"
          >
            <FormattedMessage id={getUserSelectablePresetId(currentPreset)} />
          </Button>
        </Dropdown>
      </ButtonGroup>
      <ButtonGroup>
        {zoomOut != null ? (
          <Tooltip title={<FormattedMessage id="zoom_out" />}>
            <Button
              disabled={disableZoomOut}
              type="text"
              icon={<ZoomOut16 />}
              onClick={zoomOut}
            />
          </Tooltip>
        ) : null}
        {zoomIn != null ? (
          <Tooltip title={<FormattedMessage id="zoom_in" />}>
            <Button
              disabled={disableZoomIn}
              type="text"
              icon={<ZoomIn16 />}
              onClick={zoomIn}
            />
          </Tooltip>
        ) : null}
        {shiftPrevious != null ? (
          <Tooltip title={<FormattedMessage id="shift_previous" />}>
            <Button
              type="text"
              icon={<ArrowLeft16 />}
              onClick={shiftPrevious}
            />
          </Tooltip>
        ) : null}
        {goToToday != null ? (
          <Tooltip title={<FormattedMessage id="today" />}>
            <Button type="text" onClick={goToToday} size="small">
              <FormattedMessage id="today" />
            </Button>
          </Tooltip>
        ) : null}
        {shiftNext != null ? (
          <Tooltip title={<FormattedMessage id="shift_next" />}>
            <Button type="text" icon={<ArrowRight16 />} onClick={shiftNext} />
          </Tooltip>
        ) : null}
      </ButtonGroup>
      {onExport != null ? (
        <ButtonGroup>
          <Tooltip
            title={<FormattedMessage id="resource_management.export.csv" />}
          >
            <Button type="text" icon={<Export16 />} onClick={onExport} />
          </Tooltip>
        </ButtonGroup>
      ) : null}
    </>
  );
};
